import { type ButtonHTMLAttributes, forwardRef } from 'react';

// ─── IconButton ────────────────────────────────────────────────────────────────
// Transparent-background icon button encoding the app's hover/active conventions.
// Use onPointerDown for touch-instant response alongside onClick stopPropagation.
//
// Variants:
//   default     — neutral hover (generic utility actions, reorder)
//   primary     — blue tint hover (duplicate, non-destructive primary actions)
//   destructive — warning tint hover (delete); pass isArmed for the confirm state

export type IconButtonVariant = 'default' | 'primary' | 'destructive';
export type IconButtonSize = 'xs' | 'sm' | 'field' | 'md' | 'lg';

// `shrink-0` because an icon button is SQUARE: in a crowded flex row the browser was narrowing it
// while its height held, so a 44px touch target rendered 38px wide and the icon sat off-centre.
const BASE = 'flex shrink-0 items-center justify-center rounded-md disabled:opacity-20 [&:focus:not(:focus-visible)]:outline-none';

const SIZE: Record<IconButtonSize, string> = {
  // 24px, for a control inside a line of `text-xs` where `sm` reads as the row's main event
  // rather than its furniture. Under the touch guideline on purpose and so only for a dense
  // editing list, never for anything a mis-tap would cost much on a phone.
  xs: 'h-6 w-6',
  sm: 'h-7 w-7',
  // 34px, which is a `TextField`'s height and nothing else's. That height is not a token
  // anywhere — the field derives it from `py-1.5` on a `text-sm` line — so a button standing
  // in a row of fields had no way to match it. `md` is 36 and overhangs by a pixel at each
  // edge, which reads as a misalignment rather than as a bigger control; passing `h-[2.125rem]`
  // through `className` instead would race `SIZE.md` for the same property, and the loser of
  // that race is decided by CSS source order and is silent.
  field: 'h-[2.125rem] w-[2.125rem]',
  md: 'h-9 w-9',
  // 44px: the touch-target size, for a coarse pointer on a bar where a mis-tap deletes something.
  // `md` is 36px, which is under the guideline and under what this app's own coarse controls use.
  lg: 'h-11 w-11',
};

const VARIANT_NORMAL: Record<IconButtonVariant, string> = {
  default:     'text-ws-muted hover:bg-ws-hover hover:text-ws-text active:bg-ws-hover',
  primary:     'text-ws-muted hover:bg-ws-primary/10 dark:hover:bg-ws-primary/40 hover:text-ws-primary dark:hover:text-ws-text-link',
  destructive: 'text-ws-muted hover:bg-ws-warning-fg/10 hover:text-ws-warning-fg active:bg-ws-warning-fg/20',
};

/**
 * ON, for an icon button that is a toggle rather than an action.
 *
 * The same filled treatment `ToolbarButton` already uses for bold and italic, so a teacher who
 * has learned what a filled toolbar button means reads this the same way. It is a BORDER plus a
 * fill, not a tint: `Button`'s `pressed` grammar is teal in both states, which is right for a
 * panel toggle sitting alone in a bar and not legible on a 28px square in a crowded row.
 */
const ACTIVE = 'border border-ws-btn-active-fg bg-ws-btn-active text-ws-btn-active-fg';

const VARIANT_ARMED: Record<IconButtonVariant, string> = {
  default:     '',
  primary:     '',
  // Warning, not error — a confirm-before-delete caution state, not an active
  // problem. Matches the hover tint above so the button doesn't shift color
  // families between "about to interact with this" and "about to confirm."
  destructive: 'bg-ws-warning-fg/15 text-ws-warning-fg',
};

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  isArmed?: boolean;
  /** This button is a TOGGLE and is currently on. Sets `aria-pressed` too, so a caller cannot
   *  show the state without announcing it. */
  isActive?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant = 'default', size = 'md', isArmed = false, isActive, className = '', ...props }, ref) => {
    const variantClass = isArmed && VARIANT_ARMED[variant]
      ? VARIANT_ARMED[variant]
      : isActive
        ? ACTIVE
        : VARIANT_NORMAL[variant];
    return (
      <button
        ref={ref}
        aria-pressed={isActive}
        className={`${BASE} ${SIZE[size]} ${variantClass} ${className}`}
        {...props}
      />
    );
  }
);
IconButton.displayName = 'IconButton';

// ─── ToolbarButton ─────────────────────────────────────────────────────────────
// Icon button for rich-text toolbars. Fires via onMouseDown (not onClick) so it
// doesn't steal focus from contenteditable. Supports an active/inactive toggle.

interface ToolbarButtonProps {
  onClick: () => void;
  /**
   * The popup's own aria wiring, when this button is a menu trigger.
   *
   * A picker passes `aria-haspopup='listbox'` and the id of the list it controls, which is not
   * the `dialog` the `isExpanded` branch below assumes.
   */
  'aria-haspopup'?: 'dialog' | 'listbox' | 'menu';
  label: string;
  /** Toggle button (bold, italic…) — renders aria-pressed */
  isActive?: boolean;
  /** Popup-opener button — renders aria-expanded + aria-haspopup="dialog" instead of aria-pressed */
  isExpanded?: boolean;
  /**
   * Present but inert — a capability the teacher has, which this context does not take.
   *
   * `aria-disabled`, never the native `disabled`. A natively disabled button leaves the tab order
   * and announces nothing, so the teacher who most needs to learn that something unlocks later is
   * the one who cannot reach it. Inert and REACHABLE is the point: the toolbar keeps its shape,
   * and the control can say why in its own name.
   *
   * Say why in `label`, not beside it — this app puts no explanatory note in the layout.
   */
  isDisabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * FORWARDS ITS REF, which is load-bearing and was not obvious.
 *
 * A popup anchored on this button needs the element: to place itself, to tell its own trigger
 * apart from an outside click, and to notice the trigger has been scrolled out of existence.
 * Without the forward the ref stayed null and `FlowPickerMenu` read that as "the trigger is
 * hidden" and shut itself the instant it opened — the variables menu in every block's toolbar
 * flashed up and vanished.
 */
export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(function ToolbarButton(
  { onClick, label, isActive, isExpanded, isDisabled, className = '', children, 'aria-haspopup': hasPopup },
  ref,
) {
  const visuallyActive = !isDisabled && (isActive ?? isExpanded ?? false);
  const ariaProps =
    isExpanded !== undefined
      ? { 'aria-expanded': isExpanded, 'aria-haspopup': hasPopup ?? ('dialog' as const) }
      : isActive !== undefined
      ? { 'aria-pressed': isActive }
      : {};
  return (
    <button
      type='button'
      ref={ref}
      /* Both handlers guard, not just one: this button acts on mousedown rather than click, so a
         native `disabled` would not be the whole story even if it were used. */
      onMouseDown={(e) => { e.preventDefault(); if (!isDisabled) onClick(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isDisabled) onClick(); } }}
      aria-label={label}
      aria-disabled={isDisabled || undefined}
      {...ariaProps}
      className={`flex h-9 w-full items-center justify-center rounded-lg border transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-ws-primary focus-visible:ring-offset-1 ${
        visuallyActive
          ? 'border-ws-btn-active-fg bg-ws-btn-active text-ws-btn-active-fg'
          : 'border-ws-border bg-ws-btn text-ws-text hover:border-ws-btn-active-fg hover:text-ws-btn-active-fg'
      } ${isDisabled ? 'cursor-not-allowed opacity-40' : ''} ${className}`}
    >
      {children}
    </button>
  );
});
